import { Button } from 'antd';
import { Link } from 'react-router-dom';

import type { OpenCommentModalProps } from '@app/components/Chat/components/types';
import {
  CLIENT_CARD_PATH,
  emptyFn,
  LEAD_CARD_PATH,
  USERS_PATH,
} from '@app/constants';
import type { NNotificationProvider } from '@app/providers/NotificationProvider/NotificationProvider.types';
import { handleCatchError } from '@app/services';
import type { IBaseQueryParams } from '@app/services/rtkQuery/baseQuery';
import type { NCrmTasks } from '@app/services/rtkQuery/CrmTasks/types';
import type { NActionsHistory, NAuth } from '@app/types';
import { addLeadingZeroes } from '@app/utils/addLeadingZeroes';

import type { LazyQueryTrigger } from '@reduxjs/toolkit/dist/query/react/buildHooks';
import type { QueryDefinition } from '@reduxjs/toolkit/query';

const LEAD = 'Lead';
const USER = 'User';
const CLIENT = 'Client';
const CRM_TASK = 'CrmTask';
const RECORD = 'Record';
const CLIENT_COMMENT = 'ClientComment';

const OBJECTS_TYPES_WITH_LINKS = [LEAD, USER, CLIENT];
const OBJECTS_TYPES_WITHOUT_LINKS = [CRM_TASK, RECORD, CLIENT_COMMENT];

type GetCrmTaskTrigger = LazyQueryTrigger<
  QueryDefinition<
    {
      id: number;
      ActiveBranchId: NAuth.UserPrint['activeBranchId'];
    },
    ({ url, method, data, params, headers, logs }: IBaseQueryParams) => Promise<
      | {
          data: NCrmTasks.ModelCrmTask;
          error?: undefined;
        }
      | {
          error: {
            status: string;
            data: string;
          };
          data?: undefined;
        }
    >,
    never,
    NCrmTasks.ModelCrmTask,
    'crmTasksApi'
  >
>;

interface BaseModalHandlersProps {
  objectId: number;
  activeBranchId: NAuth.UserPrint['activeBranchId'];
  showNotification: NNotificationProvider.ShowNotificationType;
  getCrmTask: GetCrmTaskTrigger;
  openPreviewTaskModal: (target: number | NCrmTasks.ModelCrmTask) => void;
  onOpenRecord(recordId: number): void;
  openCommentModal?: ({
    commentId,
    actionItems,
  }: OpenCommentModalProps) => void;
  actionItems?: NActionsHistory.ModelActionHistoryItem[] | null;
}

interface FormatActionTextProps extends BaseModalHandlersProps {
  actionText: string | null;
  objectS: string | null;
  objectType: string | null;
  actionId?: string | null;
  onCloseModal?: () => void;
}

type OpenModalByObjectTypeProps = BaseModalHandlersProps & {
  objectType: string | null;
};

export const formatActionText = ({
  actionText,
  objectS,
  objectType,
  objectId,
  getCrmTask,
  activeBranchId,
  showNotification,
  onOpenRecord,
  openPreviewTaskModal,
  openCommentModal = emptyFn,
  actionItems = null,
  actionId,
  onCloseModal,
}: FormatActionTextProps) => {
  const hasObjectInText =
    actionText && objectS && objectType && actionText.includes(objectS);

  if (hasObjectInText) {
    const [prefix, suffix = ''] = actionText!.split(objectS!);

    const objectLabelPrefix = objectType === RECORD ? `${objectS} ` : '';

    const renderObjectNode = () => {
      if (OBJECTS_TYPES_WITH_LINKS.includes(objectType!)) {
        const link = getObjectLink(objectType, objectId);

        const handleClick = () => {
          if (onCloseModal) {
            onCloseModal();
          }
        };

        return (
          <Link to={link} onClick={handleClick}>
            {objectS}
          </Link>
        );
      }

      if (OBJECTS_TYPES_WITHOUT_LINKS.includes(objectType!)) {
        const isRecord = objectType === RECORD;

        const buttonLabel = isRecord ? (
          <span data-testid="record-id">
            {addLeadingZeroes(objectId.toString())}
          </span>
        ) : (
          objectS
        );

        return (
          <Button
            type="link"
            className="inline-button"
            onClick={() =>
              openModalByObjectType({
                objectType,
                objectId,
                activeBranchId,
                showNotification,
                getCrmTask,
                onOpenRecord,
                openPreviewTaskModal,
                openCommentModal,
                actionItems,
              })
            }
          >
            {buttonLabel}
          </Button>
        );
      }

      return objectS;
    };

    return (
      <>
        {prefix}
        {objectLabelPrefix}
        {renderObjectNode()}
        {suffix}
      </>
    );
  }

  const isPaid = actionId === 'ChangeClientRecordPaid';

  return (
    <>
      {actionText}{' '}
      {isPaid && (
        <Button
          type="link"
          className="inline-button"
          onClick={() => {
            onOpenRecord(objectId);
          }}
        >
          <span data-testid="record-id">
            {addLeadingZeroes(objectId.toString())}
          </span>
        </Button>
      )}
    </>
  );
};

const openModalByObjectType = ({
  objectType,
  objectId,
  activeBranchId,
  showNotification,
  getCrmTask,
  onOpenRecord,
  openPreviewTaskModal,
  openCommentModal,
  actionItems,
}: OpenModalByObjectTypeProps) => {
  switch (objectType) {
    case CRM_TASK:
      openTaskPreviewModal({
        objectId,
        activeBranchId,
        showNotification,
        getCrmTask,
        openPreviewTaskModal,
      });
      break;
    case RECORD:
      onOpenRecord(objectId);
      break;
    case CLIENT_COMMENT:
      if (openCommentModal) {
        openCommentModal({ commentId: objectId, actionItems });
      }
      break;
    default:
      break;
  }
};

const openTaskPreviewModal = async ({
  objectId,
  activeBranchId,
  showNotification,
  getCrmTask,
  openPreviewTaskModal,
}: {
  objectId: number;
  activeBranchId: NAuth.UserPrint['activeBranchId'];
  showNotification: NNotificationProvider.ShowNotificationType;
  getCrmTask: GetCrmTaskTrigger;
  openPreviewTaskModal: (target: number | NCrmTasks.ModelCrmTask) => void;
}) => {
  const { data, error } = await getCrmTask({
    id: objectId,
    ActiveBranchId: activeBranchId,
  });

  if (error) {
    showNotification(handleCatchError(error));
  } else if (data) {
    openPreviewTaskModal(data);
  }
};

const getObjectLink = (objectType: string | null, objectId: number) => {
  switch (objectType) {
    case CLIENT:
      return `${CLIENT_CARD_PATH}?id=${objectId}`;
    case LEAD:
      return `${LEAD_CARD_PATH}?leadId=${objectId}`;
    case USER:
      return `${USERS_PATH}/edit/?id=${objectId}&key=1`;
    default:
      return '/';
  }
};
